import StockIndicator, { getStockLevel } from "../../../common/components/StockIndicator/StockIndicator";
import { Permission, userHasPermission } from "../../../utils/permissions";
import { trackPdpClick } from "../../../feature/Analytics/ga/ga";
import CircleSpinner from "../../../common/components/CircleSpinner/CircleSpinner";
import { LinkButton, LinkType } from "../../../common/components/Link";
import { DateFormatType, formatDate } from "../../../utils/DateFormatter";
import { isProductPMA } from "../../../feature/ProductDescriptor/v1/productDescriptorUtils";
import {
  selectEarliestReplenishmentLeadTimeDate,
  selectTotalInStockAmount,
} from "../../../utils/pna/availabilitySelectors";
import { useTranslation } from "react-i18next";
import type { ProductDescriptor } from "~/feature/ProductDescriptor/v1/types";

interface StockLevelSectionProps {
  productDescriptor: ProductDescriptor;
  quantity: number;
  className?: string;
  onCheckAvailabilityClick: () => void;
  checkAvailabilityDisabled?: boolean;
  showReplenishment?: boolean;
}
export const StockLevelSection = (props: StockLevelSectionProps) => {
  const { t } = useTranslation();

  const {
    productDescriptor,
    quantity,
    className,
    onCheckAvailabilityClick,
    checkAvailabilityDisabled,
    showReplenishment = true,
  } = props;

  const quantityVisible = productDescriptor.base.quantityVisible;
  const { availability: availabilityInfo, isAvailabilityLoading: isLoadingAvailability } = productDescriptor;

  const availableStock = selectTotalInStockAmount(availabilityInfo);
  const stockLevel = getStockLevel(availableStock, quantity);

  const showCheckAvailabilityLink = userHasPermission(Permission.AVAILABILITY);

  if (isProductPMA(productDescriptor) || !quantityVisible) {
    return null;
  }

  const checkAvailabilityClick = () => {
    trackPdpClick("Check availability details", productDescriptor.base.designation);
    onCheckAvailabilityClick();
  };

  const replenishmentLeadTimeDate = selectEarliestReplenishmentLeadTimeDate(availabilityInfo);

  return (
    <div className={className}>
      {isLoadingAvailability && <CircleSpinner />}
      {availabilityInfo && (
        <>
          <div className={"d-flex flex-align-center mb-2"}>
            <StockIndicator level={stockLevel} />
            {showCheckAvailabilityLink && (
              <LinkButton
                className={"ml-1"}
                variant={LinkType.PRIMARY}
                onClick={checkAvailabilityClick}
                disabled={checkAvailabilityDisabled}
              >
                {t("productDetailPage.checkAvailability")}
              </LinkButton>
            )}
          </div>
          {showReplenishment && (
            <div className="mt-1">
              {t("addProduct.replenishment", {
                date: replenishmentLeadTimeDate ? formatDate(replenishmentLeadTimeDate, DateFormatType.DATE) : "-",
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
};
